/**
 * V13.14.3 P1 — Learner Refer-a-Friend page.
 *
 * Route: /learner/refer
 *
 * Features:
 *  - Shows learner's auto-generated referral code + share URL
 *  - One-tap share via native Web Share API (falls back to WhatsApp / Clipboard)
 *  - Lifetime stats (referred / trial_paid / elite / consult conversions)
 *  - Recent 20 referred list (name + stage)
 *  - Trust-building helper copy on why there's no incentive — it's a gift, not a grift.
 *
 * No benefit tracking on-device (backend pushes to OPS funnel on enrollment sync).
 */
import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Gift, Share2, Copy, MessageCircle, Users, TrendingUp, Sparkles, Check,
} from 'lucide-react';
import { toast } from 'sonner';

export default function ReferAFriend() {
  const [code, setCode] = useState(null);
  const [shareUrl, setShareUrl] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const [c, s] = await Promise.all([
        api.get('/api/referrals/my-code'),
        api.get('/api/referrals/my-stats'),
      ]);
      setCode(c.data.code);
      setShareUrl(c.data.share_url);
      setShareMessage(c.data.share_message);
      setStats(s.data);
    } catch {
      toast.error('Could not load your referral code');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Soul Sanskar Academy',
          text: shareMessage,
          url: shareUrl,
        });
        return;
      } catch { /* user cancelled */ }
    }
    copyLink();
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareMessage);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Copied — paste anywhere you like');
    } catch {
      toast.error('Copy not supported');
    }
  };

  const whatsappShare = () => {
    const text = encodeURIComponent(shareMessage);
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener');
  };

  if (loading) return (
    <div className="space-y-4 max-w-3xl mx-auto">
      <Skeleton className="h-10 w-60" />
      <Skeleton className="h-48 rounded-2xl" />
      <Skeleton className="h-32 rounded-2xl" />
    </div>
  );

  const totals = stats?.totals || {};

  return (
    <div className="space-y-6 max-w-3xl mx-auto pb-10" data-testid="refer-page">
      <header>
        <h1 className="font-heading text-3xl font-semibold tracking-tight flex items-center gap-2">
          <Gift className="h-7 w-7 text-rose-600" />
          Share Soul Sanskar
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-lg">
          If someone you love is pregnant, planning, or newly postpartum — give them what you wish you had on day one.
        </p>
      </header>

      {/* Hero share card */}
      <Card className="overflow-hidden rounded-2xl border-rose-100 bg-gradient-to-br from-rose-50/60 via-pink-50/50 to-amber-50/40">
        <CardContent className="pt-6 pb-6">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="h-4 w-4 text-rose-600" />
            <span className="text-xs uppercase tracking-wide text-rose-900 font-medium">Your referral link</span>
          </div>
          <div
            className="font-mono text-sm bg-white/70 border border-rose-200 rounded-xl px-4 py-3 mt-2 break-all"
            data-testid="refer-share-url"
          >
            {shareUrl}
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <Button onClick={nativeShare} data-testid="refer-share-btn" className="bg-rose-600 hover:bg-rose-700">
              <Share2 className="h-4 w-4 mr-1" />Share now
            </Button>
            <Button variant="outline" onClick={whatsappShare} data-testid="refer-whatsapp-btn">
              <MessageCircle className="h-4 w-4 mr-1" />WhatsApp
            </Button>
            <Button variant="outline" onClick={copyLink} data-testid="refer-copy-btn">
              {copied ? <Check className="h-4 w-4 mr-1 text-emerald-600" /> : <Copy className="h-4 w-4 mr-1" />}
              {copied ? 'Copied' : 'Copy message'}
            </Button>
            <Badge variant="outline" className="ml-auto self-center text-[10px] border-rose-200">
              Code: {code}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <Card className="rounded-2xl" data-testid="refer-stats">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg font-heading">
            <TrendingUp className="h-5 w-5 text-rose-600" />Your impact
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Metric label="Joined" value={totals.referred} testid="stat-referred" />
            <Metric label="Trial paid" value={totals.trial_paid} testid="stat-trial" />
            <Metric label="Elite" value={totals.elite} testid="stat-elite" />
            <Metric label="Consulted" value={totals.consult} testid="stat-consult" />
          </div>
        </CardContent>
      </Card>

      {/* Recent referrals */}
      {(stats?.items || []).length > 0 && (
        <Card className="rounded-2xl" data-testid="refer-recent">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg font-heading">
              <Users className="h-5 w-5 text-rose-600" />Recent joiners
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.items.slice(0, 10).map((u, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-8 w-8 rounded-full bg-rose-100 flex items-center justify-center text-rose-700 text-xs font-medium flex-shrink-0">
                      {(u.name || '?').slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{u.name || 'A friend'}</p>
                      <p className="text-[11px] text-muted-foreground">
                        Joined {new Date(u.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <TierBadge tier={u.learner_type} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="text-[11px] text-muted-foreground text-center pt-2">
        <Sparkles className="h-3 w-3 inline mr-1" />
        No cash incentive. Soul Sanskar is a quiet gift — share only if you truly loved it.
      </div>
    </div>
  );
}

function Metric({ label, value, testid }) {
  return (
    <div className="bg-slate-50/60 border border-slate-100 rounded-xl px-4 py-3" data-testid={testid}>
      <p className="text-2xl font-semibold text-rose-700">{value || 0}</p>
      <p className="text-[11px] text-muted-foreground uppercase tracking-wide">{label}</p>
    </div>
  );
}

function TierBadge({ tier }) {
  const map = {
    elite: { label: 'Elite', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
    gold: { label: 'Elite', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
    trial_paid: { label: 'Trial', cls: 'bg-rose-100 text-rose-800 border-rose-300' },
    gynae_consult: { label: 'Consult', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
    nutritionist_consult: { label: 'Consult', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  };
  const m = map[tier] || { label: 'Free', cls: 'bg-slate-100 text-slate-700 border-slate-200' };
  return (
    <Badge variant="outline" className={`text-[10px] ${m.cls}`}>{m.label}</Badge>
  );
}
